import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { isSinger, isChordLine, chordToSpanish, parseChordPositions } from '../utils/singerFriendly';
import { extractChordSequenceWithMapping } from '../utils/chordSequence';
import { ChordPlayer } from '../components/ChordPlayer';
import { useTheme } from '../contexts/ThemeContext';
import { Button } from '../components/ui/button';
import { cn } from '../lib/utils';
import {
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  ChevronDown,
  X,
  Music,
  Eye,
  EyeOff,
  Maximize,
  Minimize,
  ListMusic,
  RotateCcw,
  ZoomIn,
  ZoomOut,
  RotateCw
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CHROMATIC_SCALE = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];

export const SingerViewPage = () => {
  const { getAuthHeaders, user } = useAuth();
  const singerMode = isSinger(user);
  const { setDarkMode } = useTheme();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [songs, setSongs] = useState([]);
  const [currentSongIndex, setCurrentSongIndex] = useState(0);
  const [currentLineIndex, setCurrentLineIndex] = useState(0);
  const [transposedKey, setTransposedKey] = useState('');
  const [showChords, setShowChords] = useState(!singerMode);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [medley, setMedley] = useState(null);
  const [showSongList, setShowSongList] = useState(false);
  const [songTransition, setSongTransition] = useState(null); // { title, theme, direction }
  const [transposeSemitones, setTransposeSemitones] = useState(0);
  const [activeChordIndex, setActiveChordIndex] = useState(-1);

  // Zoom — persisted in localStorage
  const ZOOM_MIN = 0.4;
  const ZOOM_MAX = 2.5;
  const ZOOM_STEP = 0.15;
  const ZOOM_KEY = 'singer-view-zoom';
  const [zoomLevel, setZoomLevel] = useState(() => {
    try { return parseFloat(localStorage.getItem(ZOOM_KEY)) || 1; } catch { return 1; }
  });
  const [showZoomIndicator, setShowZoomIndicator] = useState(false);
  const zoomIndicatorTimer = useRef(null);
  const zoomLevelRef = useRef(zoomLevel);
  useEffect(() => { zoomLevelRef.current = zoomLevel; }, [zoomLevel]);

  const updateZoom = useCallback((newZoom) => {
    const clamped = Math.round(Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, newZoom)) * 100) / 100;
    setZoomLevel(clamped);
    try { localStorage.setItem(ZOOM_KEY, clamped.toString()); } catch {}
    // Show indicator briefly
    setShowZoomIndicator(true);
    clearTimeout(zoomIndicatorTimer.current);
    zoomIndicatorTimer.current = setTimeout(() => setShowZoomIndicator(false), 1200);
  }, []);

  const handleZoomIn = useCallback(() => updateZoom(zoomLevel + ZOOM_STEP), [zoomLevel, updateZoom]);
  const handleZoomOut = useCallback(() => updateZoom(zoomLevel - ZOOM_STEP), [zoomLevel, updateZoom]);
  const handleZoomReset = useCallback(() => updateZoom(1), [updateZoom]);

  // Refs
  const lineRefs = useRef([]);
  const scrollContainerRef = useRef(null);
  const touchLastY = useRef(null);
  const touchAccum = useRef(0);
  const touchStartY = useRef(null);
  const touchStartTime = useRef(null);
  const lastTap = useRef(0);
  const pinchStartDist = useRef(null);
  const pinchStartZoom = useRef(null);

  // Force dark mode on mount
  useEffect(() => {
    setDarkMode();
    return () => {};
  }, []);

  useEffect(() => {
    const songId = searchParams.get('song');
    const medleyId = searchParams.get('medley');

    if (medleyId) {
      fetchMedley(medleyId);
    } else if (songId) {
      fetchSong(songId);
    } else {
      fetchRecentSongs();
    }
  }, [searchParams]);

  const fetchSong = async (songId) => {
    try {
      const response = await fetch(`${API}/songs/${songId}`, {
        credentials: 'include',
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const song = await response.json();
        setSongs([song]);
        setTransposedKey(song.original_key);
        setTransposeSemitones(0);
      }
    } catch (error) {
      console.error('Error fetching song:', error);
    }
  };

  const fetchMedley = async (medleyId) => {
    try {
      const response = await fetch(`${API}/medleys/${medleyId}`, {
        credentials: 'include',
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setMedley(data);
        const songPromises = data.song_ids.map(id =>
          fetch(`${API}/songs/${id}`, {
            credentials: 'include',
            headers: getAuthHeaders()
          }).then(r => r.ok ? r.json() : null)
        );
        const songDetails = await Promise.all(songPromises);
        setSongs(songDetails.filter(Boolean));
        if (songDetails[0]) {
          setTransposedKey(data.global_key || songDetails[0].original_key);
          setTransposeSemitones(0);
        }
      }
    } catch (error) {
      console.error('Error fetching medley:', error);
    }
  };

  const fetchRecentSongs = async () => {
    try {
      const response = await fetch(`${API}/songs?limit=10`, {
        credentials: 'include',
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setSongs(data);
        if (data[0]) {
          setTransposedKey(data[0].original_key);
          setTransposeSemitones(0);
        }
      }
    } catch (error) {
      console.error('Error fetching songs:', error);
    }
  };

  const transposeChord = useCallback((chord, semitones) => {
    const match = chord.match(/^([A-G][#b]?)(.*)/);
    if (!match) return chord;
    const [, root, suffix] = match;
    const flatToSharp = { 'Db': 'C#', 'Eb': 'D#', 'Gb': 'F#', 'Ab': 'G#', 'Bb': 'A#' };
    const normalized = flatToSharp[root] || root;
    const idx = CHROMATIC_SCALE.indexOf(normalized);
    if (idx === -1) return chord;
    const newIdx = (idx + semitones + 12) % 12;
    return CHROMATIC_SCALE[newIdx] + suffix;
  }, []);

  const currentSong = songs[currentSongIndex];
  const rawLyrics = currentSong?.lyrics || '';
  const lyrics = useMemo(() => {
    if (transposeSemitones === 0) return rawLyrics;
    return rawLyrics.split('\n').map(line => {
      // Transpose [Chord] inline format — only match actual chords, not section labels like [Coro]
      let transposed = line.replace(/\[([A-G][#b]?(?:m|maj|min|dim|aug|sus\d?|add\d+|7|9|11|13|6|\/[A-G][#b]?)*)\]/g, (_, chord) =>
        '[' + transposeChord(chord, transposeSemitones) + ']'
      );
      // Transpose chord-above-line format
      if (isChordLine(transposed)) {
        transposed = transposed.replace(/([A-G][#b]?(?:m|maj|min|dim|aug|sus\d?|add\d+|7|9|11|13|6|\/[A-G][#b]?)*)/g, (match) =>
          transposeChord(match, transposeSemitones)
        );
      }
      return transposed;
    }).join('\n');
  }, [rawLyrics, transposeSemitones, transposeChord]);

  // Build display lines: pair chord lines with their lyrics lines
  const allLines = lyrics.split('\n').filter(line => line.trim());
  const displayLines = useMemo(() => {
    const result = [];
    for (let i = 0; i < allLines.length; i++) {
      if (isChordLine(allLines[i])) {
        // This is a chord line — pair it with the next lyrics line
        const chordText = allLines[i];
        const lyricsText = (i + 1 < allLines.length && !isChordLine(allLines[i + 1]))
          ? allLines[i + 1] : '';
        if (lyricsText) {
          result.push({ type: 'paired', chord: chordText, lyrics: lyricsText });
          i++; // skip the lyrics line since we paired it
        } else {
          result.push({ type: 'chord', chord: chordText, lyrics: '' });
        }
      } else {
        result.push({ type: 'lyrics', chord: '', lyrics: allLines[i] });
      }
    }
    return result;
  }, [allLines]);

  // Extract chord sequence for MIDI player
  const { chords: chordSequence } = useMemo(
    () => extractChordSequenceWithMapping(lyrics),
    [lyrics]
  );

  // Map each displayLine to its globalChordStart and end index
  const displayLineChordRanges = useMemo(() => {
    const ranges = [];
    let counter = 0;
    for (const lineObj of displayLines) {
      if (lineObj.chord) {
        const count = parseChordPositions(lineObj.chord).length;
        ranges.push({ start: counter, end: counter + count - 1 });
        counter += count;
      } else {
        ranges.push({ start: -1, end: -1 });
      }
    }
    return ranges;
  }, [displayLines]);

  // For backward compat with renderLyricsWithChords
  const displayLineChordStarts = useMemo(
    () => displayLineChordRanges.map(r => r.start),
    [displayLineChordRanges]
  );

  const handleChordChange = useCallback((index) => {
    setActiveChordIndex(index);
    if (index < 0) return;
    // Auto-scroll: find which displayLine contains this chord index
    for (let i = 0; i < displayLineChordRanges.length; i++) {
      const { start, end } = displayLineChordRanges[i];
      if (start >= 0 && index >= start && index <= end) {
        setCurrentLineIndex(i);
        break;
      }
    }
  }, [displayLineChordRanges]);

  const lines = displayLines; // use displayLines as the main lines array
  const nextSong = songs[currentSongIndex + 1];
  const prevSong = songs[currentSongIndex - 1];
  const isNearEnd = currentLineIndex >= lines.length - 3;
  const progressPercent = lines.length > 1 ? (currentLineIndex / (lines.length - 1)) * 100 : 0;

  // Render lyrics text with chords positioned exactly above correct characters.
  // globalChordStart is the index into the global chord sequence for highlighting.
  const renderLyricsWithChords = (lyricsText, chordLine, globalChordStart = -1) => {
    const chordPositions = parseChordPositions(chordLine);
    if (!chordPositions.length) return lyricsText;

    const sorted = [...chordPositions].sort((a, b) => a.pos - b.pos);
    const segments = [];
    let lastIdx = 0;

    for (let ci = 0; ci < sorted.length; ci++) {
      const { pos, chord } = sorted[ci];
      const clampedPos = Math.min(pos, lyricsText.length);
      if (clampedPos > lastIdx) {
        segments.push({ type: 'text', content: lyricsText.substring(lastIdx, clampedPos) });
      }
      const globalIdx = globalChordStart >= 0 ? globalChordStart + ci : -1;
      segments.push({ type: 'chord', content: chord, globalIdx });
      lastIdx = clampedPos;
    }
    if (lastIdx < lyricsText.length) {
      segments.push({ type: 'text', content: lyricsText.substring(lastIdx) });
    }

    return segments.map((seg, i) => {
      if (seg.type === 'chord') {
        const name = chordToSpanish(seg.content);
        const isActive = seg.globalIdx >= 0 && seg.globalIdx === activeChordIndex;
        return (
          <span key={i} className={`relative inline cursor-help group ${isActive ? 'ring-2 ring-primary bg-primary/10 rounded px-0.5' : ''}`}>
            <span className={`absolute left-0 font-mono font-bold whitespace-nowrap ${isActive ? 'text-primary scale-110' : 'text-primary/90'}`} style={{ bottom: '100%', fontSize: '0.55em', lineHeight: 1.3, transition: 'transform 0.2s' }}>
              {seg.content}
              <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-0.5 px-2 py-1 bg-card text-card-foreground border border-border rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-50" style={{ fontSize: '12px' }}>
                {name}
              </span>
            </span>
          </span>
        );
      }
      return <span key={i}>{seg.content}</span>;
    });
  };

  const getDisplayText = (lineObj) => {
    if (!lineObj) return '';
    // Strip [inline] chords if not showing chords
    let text = lineObj.lyrics;
    if (!showChords) {
      text = text.replace(/\[[^\]]+\]/g, '');
    }
    return text;
  };

  const handleTranspose = (semitones) => {
    const isMinor = transposedKey.includes('m') && !transposedKey.includes('maj');
    let baseKey = transposedKey.replace('m', '');
    const flatToSharp = { 'Db': 'C#', 'Eb': 'D#', 'Gb': 'F#', 'Ab': 'G#', 'Bb': 'A#' };
    baseKey = flatToSharp[baseKey] || baseKey;

    const idx = CHROMATIC_SCALE.indexOf(baseKey);
    if (idx === -1) return;

    const newIdx = (idx + semitones + 12) % 12;
    setTransposedKey(CHROMATIC_SCALE[newIdx] + (isMinor ? 'm' : ''));
    setTransposeSemitones(prev => prev + semitones);
  };

  const triggerSongTransition = useCallback((song, direction, callback) => {
    setSongTransition({ title: song.title, theme: song.theme, direction });
    setTimeout(() => {
      callback();
      setTimeout(() => {
        setSongTransition(null);
      }, 600);
    }, 500);
  }, []);

  const handleNextLine = useCallback(() => {
    if (currentLineIndex < lines.length - 1) {
      setCurrentLineIndex(prev => prev + 1);
    } else if (currentSongIndex < songs.length - 1) {
      const nextSongObj = songs[currentSongIndex + 1];
      if (nextSongObj) {
        triggerSongTransition(nextSongObj, 'next', () => {
          setCurrentSongIndex(prev => prev + 1);
          setCurrentLineIndex(0);
          setTransposedKey(nextSongObj.original_key);
          setTransposeSemitones(0);
        });
      }
    }
  }, [currentLineIndex, currentSongIndex, lines.length, songs, triggerSongTransition]);

  const handlePrevLine = useCallback(() => {
    if (currentLineIndex > 0) {
      setCurrentLineIndex(prev => prev - 1);
    } else if (currentSongIndex > 0) {
      const pSong = songs[currentSongIndex - 1];
      if (pSong) {
        triggerSongTransition(pSong, 'prev', () => {
          setCurrentSongIndex(prev => prev - 1);
          const prevLines = pSong.lyrics.split('\n').filter(l => l.trim()) || [];
          setCurrentLineIndex(prevLines.length - 1);
          setTransposedKey(pSong.original_key);
          setTransposeSemitones(0);
        });
      }
    }
  }, [currentLineIndex, currentSongIndex, songs, triggerSongTransition]);

  const handleRestart = useCallback(() => {
    setCurrentLineIndex(0);
  }, []);

  const handleGoToSong = useCallback((idx) => {
    if (idx === currentSongIndex) {
      setShowSongList(false);
      return;
    }
    setShowSongList(false);
    const targetSong = songs[idx];
    if (targetSong) {
      triggerSongTransition(targetSong, idx > currentSongIndex ? 'next' : 'prev', () => {
        setCurrentSongIndex(idx);
        setCurrentLineIndex(0);
        setTransposedKey(targetSong.original_key);
        setTransposeSemitones(0);
      });
    }
  }, [songs, currentSongIndex, triggerSongTransition]);

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  // Auto-scroll active line to center
  useEffect(() => {
    if (lineRefs.current[currentLineIndex]) {
      lineRefs.current[currentLineIndex].scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });
    }
  }, [currentLineIndex]);

  // ========== TOUCH HANDLING ==========
  // Two separate gesture modes: PINCH (2 fingers) vs SCROLL (1 finger)
  // Key: if 2 fingers detected at ANY point in gesture → pinch mode for entire gesture
  const DRAG_THRESHOLD = 50;
  const gestureMode = useRef('none'); // 'none' | 'scroll' | 'pinch'

  const getTouchDist = (t1, t2) => {
    const dx = t1.clientX - t2.clientX;
    const dy = t1.clientY - t2.clientY;
    return Math.sqrt(dx * dx + dy * dy);
  };

  const startPinch = (e) => {
    gestureMode.current = 'pinch';
    pinchStartDist.current = getTouchDist(e.touches[0], e.touches[1]);
    pinchStartZoom.current = zoomLevelRef.current;
    // Reset scroll state so it doesn't fire
    touchStartY.current = null;
    touchLastY.current = null;
    touchAccum.current = 0;
  };

  const handleTouchStart = (e) => {
    // 2 fingers from the start → immediate pinch
    if (e.touches.length >= 2) {
      startPinch(e);
      return;
    }
    // 1 finger — tentatively scroll, but can switch to pinch
    gestureMode.current = 'scroll';
    touchStartY.current = e.touches[0].clientY;
    touchLastY.current = e.touches[0].clientY;
    touchAccum.current = 0;
    touchStartTime.current = Date.now();
  };

  const handleTouchMove = (e) => {
    e.preventDefault();

    // If 2+ fingers appear mid-gesture, switch to pinch immediately
    if (e.touches.length >= 2) {
      if (gestureMode.current !== 'pinch') {
        startPinch(e);
        return;
      }
      // Already in pinch mode — update zoom
      if (pinchStartDist.current !== null) {
        const currentDist = getTouchDist(e.touches[0], e.touches[1]);
        const ratio = currentDist / pinchStartDist.current;
        updateZoom(pinchStartZoom.current * ratio);
      }
      return;
    }

    // 1 finger — only scroll if gesture is still in scroll mode
    if (gestureMode.current !== 'scroll') return;
    if (touchLastY.current === null) return;

    const currentY = e.touches[0].clientY;
    const delta = touchLastY.current - currentY;
    touchAccum.current += delta;
    touchLastY.current = currentY;

    while (touchAccum.current > DRAG_THRESHOLD) {
      touchAccum.current -= DRAG_THRESHOLD;
      handleNextLine();
    }
    while (touchAccum.current < -DRAG_THRESHOLD) {
      touchAccum.current += DRAG_THRESHOLD;
      handlePrevLine();
    }
  };

  const handleTouchEnd = (e) => {
    // If still fingers remaining, don't reset yet
    if (e.touches.length > 0) return;

    // Pinch ended — clean up, no scroll/tap action
    if (gestureMode.current === 'pinch') {
      gestureMode.current = 'none';
      pinchStartDist.current = null;
      pinchStartZoom.current = null;
      touchStartY.current = null;
      return;
    }

    // Scroll ended — check for tap
    if (gestureMode.current === 'scroll' && touchStartY.current !== null) {
      const diff = touchStartY.current - e.changedTouches[0].clientY;
      const elapsed = Date.now() - (touchStartTime.current || 0);

      // Tap (barely moved, quick)
      if (Math.abs(diff) < 10 && elapsed < 300) {
        const now = Date.now();
        if (now - lastTap.current < 300) {
          handleRestart();
          lastTap.current = 0;
        } else {
          lastTap.current = now;
          handleNextLine();
        }
      }
    }

    // Full reset
    gestureMode.current = 'none';
    touchStartY.current = null;
    touchLastY.current = null;
    touchAccum.current = 0;
    touchStartTime.current = null;
    pinchStartDist.current = null;
    pinchStartZoom.current = null;
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      switch (e.key) {
        case 'ArrowRight':
        case 'ArrowDown':
        case ' ':
          e.preventDefault();
          handleNextLine();
          break;
        case 'ArrowLeft':
        case 'ArrowUp':
          e.preventDefault();
          handlePrevLine();
          break;
        case 'Home':
        case 'r':
          e.preventDefault();
          handleRestart();
          break;
        case '+':
        case '=':
          e.preventDefault();
          handleZoomIn();
          break;
        case '-':
        case '_':
          e.preventDefault();
          handleZoomOut();
          break;
        case '0':
          e.preventDefault();
          handleZoomReset();
          break;
        case 'Escape':
          if (showSongList) {
            setShowSongList(false);
          } else {
            navigate(-1);
          }
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleNextLine, handlePrevLine, handleRestart, handleZoomIn, handleZoomOut, handleZoomReset, showSongList, navigate]);

  // Helper: get style for each line based on distance from active
  const getLineStyle = (idx) => {
    const dist = Math.abs(idx - currentLineIndex);
    if (dist === 0) {
      return {
        opacity: 1,
        filter: 'blur(0px)',
        transform: 'scale(1)',
      };
    }
    // Gradual fade: closer lines are more visible
    const opacity = Math.max(0.08, 0.55 - dist * 0.12);
    const blur = Math.min(dist * 1.2, 4);
    const scale = Math.max(0.92, 1 - dist * 0.015);
    return {
      opacity,
      filter: `blur(${blur}px)`,
      transform: `scale(${scale})`,
    };
  };

  if (songs.length === 0) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center" data-testid="singer-view-empty">
        <div className="text-center">
          <Music className="w-16 h-16 text-muted-foreground/30 mx-auto mb-4" />
          <p className="text-muted-foreground mb-4">No hay canciones seleccionadas</p>
          <Button onClick={() => navigate('/songs')}>
            Ir a canciones
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col select-none" data-testid="singer-view">
      {/* Top Controls */}
      <div className="fixed top-0 left-0 right-0 p-3 sm:p-4 flex items-center justify-between z-50 bg-gradient-to-b from-background via-background/90 to-transparent pointer-events-none">
        <div className="pointer-events-auto">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)} data-testid="exit-singer-view">
            <X className="w-6 h-6" />
          </Button>
        </div>

        <div className="flex items-center gap-1 sm:gap-2 pointer-events-auto">
          {/* Song list button (medley) */}
          {songs.length > 1 && (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setShowSongList(true)}
              className="relative"
              data-testid="song-list-btn"
            >
              <ListMusic className="w-5 h-5" />
              <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-primary text-[10px] font-bold flex items-center justify-center text-primary-foreground">
                {currentSongIndex + 1}
              </span>
            </Button>
          )}

          {/* Transpose controls */}
          {!singerMode && (
            <div className="flex items-center gap-1 bg-muted/30 rounded-lg p-1">
              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleTranspose(-1)} data-testid="transpose-down">
                <ChevronDown className="w-4 h-4" />
              </Button>
              <span className="px-2 sm:px-3 font-mono font-bold text-primary text-sm">{transposedKey} <span className="text-xs font-normal text-muted-foreground">({chordToSpanish(transposedKey)})</span></span>
              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleTranspose(1)} data-testid="transpose-up">
                <ChevronUp className="w-4 h-4" />
              </Button>
              {transposeSemitones !== 0 && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-muted-foreground"
                  onClick={() => { setTransposedKey(currentSong?.original_key || ''); setTransposeSemitones(0); }}
                  title="Restablecer tono original"
                  data-testid="transpose-reset"
                >
                  <RotateCcw className="w-4 h-4" />
                </Button>
              )}
            </div>
          )}

          {/* Toggle chords */}
          {!singerMode && (
            <Button variant="ghost" size="icon" onClick={() => setShowChords(!showChords)} data-testid="toggle-chords">
              {showChords ? <Eye className="w-5 h-5" /> : <EyeOff className="w-5 h-5" />}
            </Button>
          )}

          {/* Zoom controls — desktop */}
          <div className="hidden sm:flex items-center gap-0.5 bg-muted/30 rounded-lg p-1">
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={handleZoomOut}
              disabled={zoomLevel <= ZOOM_MIN}
              title="Reducir (−)"
              data-testid="zoom-out-btn"
            >
              <ZoomOut className="w-4 h-4" />
            </Button>
            <button
              onClick={handleZoomReset}
              className="px-2 min-w-[3rem] text-center font-mono font-semibold text-xs text-primary hover:text-primary/80 transition-colors"
              title="Restablecer zoom (0)"
              data-testid="zoom-reset-btn"
            >
              {Math.round(zoomLevel * 100)}%
            </button>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={handleZoomIn}
              disabled={zoomLevel >= ZOOM_MAX}
              title="Ampliar (+)"
              data-testid="zoom-in-btn"
            >
              <ZoomIn className="w-4 h-4" />
            </Button>
          </div>

          {/* Fullscreen */}
          <Button variant="ghost" size="icon" onClick={toggleFullscreen} data-testid="toggle-fullscreen">
            {isFullscreen ? <Minimize className="w-5 h-5" /> : <Maximize className="w-5 h-5" />}
          </Button>
        </div>
      </div>

      {/* ====== SONG TITLE + THEME — Fixed below top controls ====== */}
      <div className="fixed top-12 sm:top-14 left-0 right-0 z-40 text-center pointer-events-none px-14 py-1.5 bg-gradient-to-b from-background/90 via-background/60 to-transparent">
        <h2 className="font-heading text-xl sm:text-2xl md:text-3xl font-bold text-foreground/95 truncate">{currentSong?.title}</h2>
        <div className="flex items-center justify-center gap-2 mt-1">
          {currentSong?.theme && (
            <span className="text-sm sm:text-base md:text-lg text-primary/80 font-semibold">{currentSong.theme}</span>
          )}
          {currentSong?.theme && medley && <span className="text-muted-foreground/40 text-base">•</span>}
          {medley && (
            <span className="text-xs sm:text-sm text-muted-foreground/60">{medley.title}</span>
          )}
        </div>
      </div>

      {/* ====== LYRICS VIEW — All lines visible, active centered ====== */}
      <div
        ref={scrollContainerRef}
        className="flex-1 overflow-hidden relative touch-none"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onClick={handleNextLine}
      >
        {/* Top fade gradient */}
        <div className="absolute top-0 left-0 right-0 h-32 bg-gradient-to-b from-background via-background/70 to-transparent z-10 pointer-events-none" />

        {/* Bottom fade gradient */}
        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-background via-background/70 to-transparent z-10 pointer-events-none" />

        {/* Scrollable lyrics container */}
        <div className="h-full overflow-y-hidden flex flex-col items-center justify-start px-2 sm:px-6">
          {/* Spacer top — pushes first line to center */}
          <div className="shrink-0" style={{ height: '42vh' }} />

          {lines.map((lineObj, idx) => {
            const dist = Math.abs(idx - currentLineIndex);
            // Base sizes in rem — active, adjacent, distant
            let baseSizeRem;
            if (dist === 0) baseSizeRem = 3.2; // ~text-5xl
            else if (dist === 1) baseSizeRem = 1.8; // ~text-3xl
            else baseSizeRem = 1.15; // ~text-xl

            const displayText = getDisplayText(lineObj);
            const hasChordAbove = showChords && lineObj.chord;

            return (
              <div
                key={`${currentSongIndex}-${idx}`}
                ref={el => lineRefs.current[idx] = el}
                onClick={(e) => { e.stopPropagation(); setCurrentLineIndex(idx); }}
                className={cn(
                  'w-full max-w-6xl text-center py-2 sm:py-3 cursor-pointer',
                  'transition-all duration-500 ease-out'
                )}
                style={getLineStyle(idx)}
              >
                {/* Lyrics line (with chords positioned inline above if applicable) */}
                <p
                  className={cn(
                    'transition-all duration-500 ease-out relative',
                    dist === 0
                      ? 'font-bold text-foreground'
                      : 'text-muted-foreground',
                    hasChordAbove ? 'pt-[0.7em]' : ''
                  )}
                  style={{
                    fontSize: `min(${baseSizeRem * zoomLevel}rem, ${dist === 0 ? '18vw' : dist === 1 ? '10vw' : '7vw'})`,
                    lineHeight: dist === 0 ? 1.15 : 1.3,
                    overflowWrap: 'break-word',
                    wordBreak: 'break-word',
                  }}
                >
                  {hasChordAbove
                    ? renderLyricsWithChords(displayText, lineObj.chord, displayLineChordStarts[idx])
                    : displayText
                  }
                </p>
              </div>
            );
          })}

          {/* Spacer bottom */}
          <div className="shrink-0" style={{ height: '42vh' }} />
        </div>

        {/* "Next song" indicator near end */}
        {isNearEnd && songs.length > 1 && (
          <div className="absolute bottom-36 left-1/2 -translate-x-1/2 text-center z-20 animate-fade-in pointer-events-none">
            {nextSong ? (
              <p className="text-sm text-muted-foreground">
                Siguiente: <span className="font-semibold text-primary">{nextSong.title}</span>
              </p>
            ) : (
              <p className="text-sm text-yellow-400 font-medium">Última canción del medley</p>
            )}
          </div>
        )}
      </div>

      {/* ====== SONG LIST DRAWER ====== */}
      {showSongList && (
        <>
          <div
            className="fixed inset-0 bg-black/60 z-50"
            onClick={() => setShowSongList(false)}
          />
          <div className="fixed top-0 right-0 bottom-0 w-[75vw] sm:w-80 bg-background border-l border-border z-50 flex flex-col animate-in slide-in-from-right duration-200">
            <div className="p-4 border-b border-border flex items-center justify-between">
              <h3 className="font-heading font-bold text-lg">
                {medley ? medley.title : 'Canciones'}
              </h3>
              <Button variant="ghost" size="icon" onClick={() => setShowSongList(false)}>
                <X className="w-5 h-5" />
              </Button>
            </div>
            <div className="flex-1 overflow-y-auto p-2">
              {songs.map((song, idx) => (
                <button
                  key={song.song_id}
                  onClick={() => handleGoToSong(idx)}
                  className={cn(
                    'w-full text-left px-4 py-3 rounded-lg mb-1 transition-colors flex items-center gap-3',
                    idx === currentSongIndex
                      ? 'bg-primary/15 text-primary font-semibold'
                      : idx < currentSongIndex
                      ? 'text-muted-foreground/50 hover:bg-muted/30'
                      : 'text-foreground hover:bg-muted/30'
                  )}
                >
                  <span className={cn(
                    'w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold shrink-0',
                    idx === currentSongIndex
                      ? 'bg-primary text-primary-foreground'
                      : idx < currentSongIndex
                      ? 'bg-muted text-muted-foreground'
                      : 'bg-muted/50 text-muted-foreground'
                  )}>
                    {idx + 1}
                  </span>
                  <span className="truncate text-sm">{song.title}</span>
                  {idx === currentSongIndex && (
                    <span className="ml-auto shrink-0 w-2 h-2 rounded-full bg-primary animate-pulse" />
                  )}
                </button>
              ))}
            </div>
          </div>
        </>
      )}

      {/* ====== ZOOM INDICATOR ====== */}
      {showZoomIndicator && (
        <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-[55] pointer-events-none">
          <div className="bg-black/70 backdrop-blur-md rounded-2xl px-6 py-4 flex flex-col items-center gap-2 animate-fade-in">
            <div className="flex items-center gap-3">
              {zoomLevel > 1 ? (
                <ZoomIn className="w-6 h-6 text-primary" />
              ) : zoomLevel < 1 ? (
                <ZoomOut className="w-6 h-6 text-primary" />
              ) : (
                <RotateCw className="w-5 h-5 text-primary" />
              )}
              <span className="text-3xl font-bold font-mono text-white">
                {Math.round(zoomLevel * 100)}%
              </span>
            </div>
            {/* Mini bar */}
            <div className="w-32 h-1.5 bg-white/20 rounded-full overflow-hidden">
              <div
                className="h-full bg-primary rounded-full transition-all duration-200"
                style={{ width: `${((zoomLevel - ZOOM_MIN) / (ZOOM_MAX - ZOOM_MIN)) * 100}%` }}
              />
            </div>
          </div>
        </div>
      )}

      {/* ====== SONG TRANSITION OVERLAY ====== */}
      {songTransition && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center pointer-events-none">
          <div className="absolute inset-0 bg-background/95 animate-song-transition-bg" />
          <div className={cn(
            'relative z-10 text-center px-8',
            songTransition.direction === 'next' ? 'animate-song-transition-in' : 'animate-song-transition-in-reverse'
          )}>
            <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl bg-primary/20 flex items-center justify-center mx-auto mb-5">
              <Music className="w-8 h-8 sm:w-10 sm:h-10 text-primary" />
            </div>
            <p className="text-sm sm:text-base text-muted-foreground/70 font-medium uppercase tracking-wider mb-2">
              {songTransition.direction === 'next' ? 'Siguiente canción' : 'Canción anterior'}
            </p>
            <h2 className="font-heading text-3xl sm:text-4xl md:text-5xl font-bold text-foreground mb-3">
              {songTransition.title}
            </h2>
            {songTransition.theme && (
              <p className="text-lg sm:text-xl text-primary/80 font-medium">{songTransition.theme}</p>
            )}
          </div>
        </div>
      )}

      {/* ====== BOTTOM BAR ====== */}
      <div className="fixed bottom-0 left-0 right-0 z-40 pointer-events-none">
        {/* Progress bar */}
        <div className="h-1 bg-muted/30">
          <div
            className="h-full bg-primary transition-all duration-500 ease-out"
            style={{ width: `${progressPercent}%` }}
          />
        </div>

        {/* Next song hint near end */}
        {isNearEnd && songs.length > 1 && (
          <div className="bg-background/80 px-4 py-1 text-center">
            {nextSong ? (
              <p className="text-xs text-muted-foreground">
                Siguiente: <span className="font-semibold text-primary">{nextSong.title}</span>
              </p>
            ) : (
              <p className="text-xs text-yellow-400">Última canción</p>
            )}
          </div>
        )}

        {/* Chord Player — only for musicians with chords visible */}
        {!singerMode && showChords && chordSequence.length > 0 && (
          <div className="px-3 sm:px-4 py-1.5 pointer-events-auto bg-background/90">
            <ChordPlayer
              chordSequence={chordSequence}
              defaultBpm={currentSong?.bpm || 100}
              onChordChange={handleChordChange}
            />
          </div>
        )}

        {/* Navigation buttons */}
        <div className="bg-gradient-to-t from-background via-background/95 to-background/80 px-3 sm:px-4 py-3 sm:py-4 flex items-center justify-between gap-2 pointer-events-auto">
          {/* Restart button */}
          <Button
            variant="ghost"
            size="icon"
            onClick={(e) => { e.stopPropagation(); handleRestart(); }}
            disabled={currentLineIndex === 0}
            className="h-10 w-10 sm:h-11 sm:w-11 shrink-0"
            title="Volver al inicio"
            data-testid="restart-btn"
          >
            <RotateCcw className="w-5 h-5" />
          </Button>

          {/* Prev/Next */}
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="lg"
              onClick={(e) => { e.stopPropagation(); handlePrevLine(); }}
              disabled={currentLineIndex === 0 && currentSongIndex === 0}
              className="px-4 sm:px-6 h-11"
              data-testid="prev-line-btn"
            >
              <ChevronLeft className="w-5 h-5 sm:mr-1" />
              <span className="hidden sm:inline">Anterior</span>
            </Button>
            <Button
              size="lg"
              onClick={(e) => { e.stopPropagation(); handleNextLine(); }}
              disabled={currentLineIndex === lines.length - 1 && currentSongIndex === songs.length - 1}
              className="px-4 sm:px-6 h-11"
              data-testid="next-line-btn"
            >
              <span className="hidden sm:inline">Siguiente</span>
              <ChevronRight className="w-5 h-5 sm:ml-1" />
            </Button>
          </div>

          {/* Line counter */}
          <span className="text-xs text-muted-foreground whitespace-nowrap shrink-0">
            {currentLineIndex + 1}/{lines.length}
          </span>
        </div>
      </div>
    </div>
  );
};

export default SingerViewPage;
